import { CirclePlay, CircleStop } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router";
import {
  getProcessErrorDisplayMessage,
  getProcessResultDescription,
  getSimpleISO,
  ITEM_PROCESS_HIGHLIGHT_TRANSITION,
  isDryRunProcessLog,
} from "../../../shared/lib/helpers";
import { useItemContext } from "../../items/model/ItemContext";
import { ProcessStatusIcon } from "./ProcessStatusIcon";

interface SideBarLogItemProps {
  message: any;
  index: number;
  collapsed?: boolean;
  onOpenItemEdit?: (event: React.MouseEvent<HTMLAnchorElement>, itemId: string) => void;
}

const ShopSiteBadge = ({ shopSite }: { shopSite?: string }) => {
  if (shopSite === "smartstore") {
    return (
      <span
        className="badge market-badge market-smartstore"
        title="스마트스토어"
      >
        S
      </span>
    );
  }
  if (shopSite === "auction") {
    return (
      <span
        className="badge market-badge market-auction"
        title="옥션"
      >
        A
      </span>
    );
  }
  if (shopSite === "gmarket") {
    return (
      <span
        className="badge market-badge market-gmarket"
        title="지마켓"
      >
        G
      </span>
    );
  }
  if (shopSite === "eleventh") {
    return (
      <span
        className="badge market-badge market-eleventh"
        title="11번가"
      >
        11
      </span>
    );
  }
  if (shopSite === "ssgdotcom") {
    return <span className="badge market-badge market-ssgdotcom" title="SSG닷컴">S</span>;
  }
  if (shopSite === "lotteon") {
    return <span className="badge market-badge market-lotteon" title="롯데ON">L</span>;
  }
  return null;
};

const SideBarLogItem = (props: SideBarLogItemProps) => {
  const { message, index, collapsed, onOpenItemEdit } = props;
  const { highlightedItem } = useItemContext();
  const logItemId = message.item_id ?? message.item?.id;
  // 목록 행 process 하이라이트와 동일 신호로, 최신 로그 1건만 표시
  const isHighlighted =
    index === 0 &&
    highlightedItem?.type === "process" &&
    logItemId != null &&
    String(highlightedItem.itemId) === String(logItemId);

  const wrapperStyle = isHighlighted
    ? {
        backgroundColor: "var(--item-process-highlight-color)",
        transition: ITEM_PROCESS_HIGHLIGHT_TRANSITION,
      }
    : { transition: ITEM_PROCESS_HIGHLIGHT_TRANSITION };

  // 축소 상태에서 아이콘 반환
  const getCollapsedIcon = () => {
    if (message.category === 'is_running_true') {
      return (
        <CirclePlay className="text-success" size={20} />
      );
    } else if (message.category === 'is_running_false') {
      return (
        <CircleStop className="text-muted" size={20} />
      );
    } else if (message.category === 'item_process') {
      return <ProcessStatusIcon isSuccess={message.is_success} isDryRun={isDryRunProcessLog(message)} />;
    } else if (message.category === 'item_process_error') {
      if (isDryRunProcessLog(message)) return <ProcessStatusIcon isSuccess={false} isDryRun />;
      return '⚠️';
    }
    return '📝';
  };

  if (collapsed) {
    return (
      <div
        className="px-2 py-1 mb-1 d-flex justify-content-center"
        style={wrapperStyle}
        title={message.message || message.item_name || '작업이력'}
      >
        <span style={{ fontSize: '20px' }}>
          {getCollapsedIcon()}
        </span>
      </div>
    );
  }

  return (
    <div className="px-2 py-1 mb-1" style={wrapperStyle}>
      <div>
        {message.category === 'is_running_true' || message.category === 'is_running_false' ? (
          <div>
            <small className="text-muted">
              {getSimpleISO(message.finished_at)}
            </small>
            <div className="d-flex align-items-center gap-1">
              {message.category === 'is_running_true' ? (
                <>
                  <CirclePlay
                    className="text-success"
                    size={20}
                  />
                  <span>작업 실행</span>
                </>
              ) : (
                <>
                  <CircleStop
                    className="text-muted"
                    size={20}
                  />
                  <span>작업 정지</span>
                </>
              )}
            </div>
          </div>
        ) : null}

        {message.category === 'item_process' ? (
          <ItemProcessLogItem message={message} onOpenItemEdit={onOpenItemEdit} />
        ) : null}
        {message.category === 'item_process_error' ? (
          <div>
            <small className="text-muted">
              {getSimpleISO(message.finished_at)}
            </small>
            <div className="d-flex align-items-center gap-1">
              {isDryRunProcessLog(message) && <ProcessStatusIcon isSuccess={false} isDryRun />}
              {`작업 오류`}
            </div>
            {message.message && (
              <small className="text-muted d-block mt-1">
                {getProcessErrorDisplayMessage(message.message)}
              </small>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export interface ItemProcessLogItemMessage {
  item_id: string;
  item?: { id: string, shop_item_id?: string, name: string };
  shop_item_id?: string;
  item_shop_id?: string;
  item_name: string;
  shop_site?: string;
  is_success: boolean;
  is_dry_run?: boolean;
  adjust_strategy?: string;
  price_decision_reason?: string;
  adjust_amount?: number;
  adjust_amount_after_limit?: number;
  price?: number;
  discount?: number;
  price_after_limit?: number;
  price_after_adjust?: number;
  discount_after_limit?: number;
  discount_after_adjust?: number;
  discount_clamped_to_zero?: boolean;
  price_ll?: number;
  price_hl?: number;
  discount_ll?: number;
  discount_hl?: number;
  error_message?: string;
  message?: string; // ProcessLog(DB)와 성공 ItemProcess WS 결과 문구
  error?: string;
  started_at: string;
  finished_at: string;
}

const ItemProcessLogItem = (props: { message: ItemProcessLogItemMessage; onOpenItemEdit?: (event: React.MouseEvent<HTMLAnchorElement>, itemId: string) => void }) => {
  const { message, onOpenItemEdit } = props;
  const navigate = useNavigate();
  const location = useLocation();
  const { focusItemRow } = useItemContext();
  const itemId = message.item_id || message.item?.id;
  const shopItemId = message.shop_item_id || message.item_shop_id || message.item?.shop_item_id;
  const errorMessage = message.error_message || message.message;
  const errorDisplayMessage = getProcessErrorDisplayMessage(errorMessage);

  const handleFocusInList = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (!itemId) {
      return;
    }
    if (focusItemRow(itemId)) {
      return;
    }
    const onItemList =
      location.pathname === "/" || location.pathname === "/items/";
    if (onItemList) {
      // 현재 필터로 목록에 없으면 필터를 비우고 다시 포커스
      navigate(
        { pathname: "/items/", search: "" },
        { state: { focusItemId: String(itemId) } }
      );
      return;
    }
    navigate("/items/", { state: { focusItemId: String(itemId) } });
  };

  return (
    <div>
      <small className="text-muted">
        {getSimpleISO(message.finished_at)}{" "}
        {shopItemId && (
          <>
            |{" "}
            {itemId ? (
              <Link
                to={`/items/${itemId}`}
                className="text-muted text-decoration-underline"
                onClick={onOpenItemEdit ? (event) => onOpenItemEdit(event, String(itemId)) : undefined}
              >
                {shopItemId}
              </Link>
            ) : (
              shopItemId
            )}
          </>
        )}
      </small>

      <div className="ps-0">
        {itemId ? (
          <button
            type="button"
            className="btn btn-link p-0 align-baseline text-decoration-none text-body sidebar-log-item-product"
            onClick={handleFocusInList}
            title={message.item_name}
          >
            <ShopSiteBadge shopSite={message.shop_site} />
            <span className="sidebar-log-item-name">{message.item_name}</span>
          </button>
        ) : (
          <span className="sidebar-log-item-product" title={message.item_name}>
            <ShopSiteBadge shopSite={message.shop_site} />
            <span className="sidebar-log-item-name">{message.item_name}</span>
          </span>
        )}
      </div>

      <div className="mt-1">
        <ul className="list-unstyled d-flex gap-1 mb-0">
          <li key="status" className="d-flex align-items-center">
            {message.is_success && <ProcessStatusIcon isSuccess isDryRun={isDryRunProcessLog(message)} />}
            {message.is_success && (
              <small className="ms-2">
                {getProcessResultDescription(message)}
              </small>
            )
            }
            {message.is_success === false && (
              <>
                <ProcessStatusIcon isSuccess={false} isDryRun={isDryRunProcessLog(message)} />
                {errorDisplayMessage && (
                  <small className="ms-2" title={errorMessage}>
                    {errorDisplayMessage}
                  </small>
                )}
              </>
            )}
          </li>
        </ul>
      </div>
    </div>
  );
};

export default SideBarLogItem;
